import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { NgFor, NgIf, TitleCasePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatDialog, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { ContentService } from '../../services/content.service';
import { DisctionaryContent } from 'src/app/services/sitecoreDictionary/dictionary-content.service';
import { CamService } from 'src/app/services/camService/cam.service';
import { CommonService } from 'src/app/shared/common/common.service';
import { ToastrApiService } from 'src/app/shared/common/toastr-api.service';
import { PaymentContentService } from "src/app/services/paymentContent/payment-content.service";

@Component({
  selector: 'app-loggedin-devices',
  templateUrl: './loggedin-devices.component.html',
  styleUrls: ['./loggedin-devices.component.scss'],
  imports: [ReactiveFormsModule, FormsModule, NgFor, NgIf, MatIcon, MatButton, MatDialogContent, MatDialogActions, MatProgressSpinner, TitleCasePipe]
})
export class LoggedinDevicesComponent implements OnInit, OnDestroy {
    paymentContent: any;
  @ViewChild('revokeWarning') revokeWarning!: TemplateRef<any>;
  content: any;
  dictionary: any;
  items: any[] = [];
  isLoading = false;
  isRevoking = false;
  private deletingId: string | null = null;
  private selectedDevice: any = null;
  private dialogRef: MatDialogRef<any> | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly paymentContentService: PaymentContentService, private contentService: ContentService,
    private readonly dictionaryContentService: DisctionaryContent,
    private readonly camService: CamService,
    private readonly commonService: CommonService,
    private readonly toastrApiService: ToastrApiService,
    private readonly dialog: MatDialog
  ) { }

  ngOnInit() {
    combineLatest({
      content: this.contentService.content$, dictionary: this.dictionaryContentService.content$
    }).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      this.content = {
        ...res.content,
        dictionary: res.dictionary
      }
    });
    this.loadPasskeys();
      this.paymentContentService.content$.subscribe((res: any) => { this.paymentContent = res; });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadPasskeys(): void {
    const userId = this.commonService.getCiamId()?.trim();
    if (!userId) {
      this.items = [];
      return;
    }
    this.isLoading = true;
    this.camService.getPasskeys(userId).then((res: any) => {
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      this.items = list;
    }).catch(() => {
      this.items = [];
      this.toastrApiService.show('error', 'Unable to load devices.');
    }).finally(() => {
      this.isLoading = false;
    });
  }

  formatCreatedAt(createdAt: string | undefined): string {
    if (!createdAt) {
      return '';
    }
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) {
      return createdAt;
    }
    const day = date.getDate();
    const month = date.toLocaleString('en-GB', { month: 'long' });
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const meridiem = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;
    return `${day} ${month} ${year}, ${String(hours).padStart(2, '0')}:${minutes}:${seconds} ${meridiem}`;
  }

  isDeleting(item: any): boolean {
    return !!item?.id && this.deletingId === item.id;
  }

  removeItem(item: any) {
    if (!item?.id || this.deletingId) {
      return;
    }
    this.selectedDevice = item;
    this.deletingId = item.id;
    this.dialogRef = this.dialog.open(this.revokeWarning, {
      disableClose: true,
      width: '448px',
      maxWidth: '448px',
      autoFocus: false,
      panelClass: 'revoke-device-dialog'
    });
  }

  closeRevokeDialog(): void {
    if (this.isRevoking) {
      return;
    }
    this.dialog.closeAll();
    this.dialogRef = null;
    this.deletingId = null;
    this.selectedDevice = null;
  }

  confirmRevoke(): void {
    const userId = this.commonService.getCiamId()?.trim();
    const authenticationMethodId = this.selectedDevice?.id;
    if (!userId || !authenticationMethodId || this.isRevoking) {
      return;
    }
    this.isRevoking = true;
    this.camService.deletePasskey(userId, authenticationMethodId).then((res: any) => {
      const status = res?.status;
      const isSuccess = status === 200 || status === '200' || status === 'Success' || status === 'success' || status == null;
      if (!isSuccess) {
        this.toastrApiService.show('error', res?.message || 'Unable to revoke device.');
        return;
      }
      this.dialog.closeAll();
      this.dialogRef = null;
      this.toastrApiService.show('success', 'Device has been revoked successfully!');
      this.loadPasskeys();
    }).catch((error: any) => {
      const status = error?.status;
      if (status === 200) {
        this.dialog.closeAll();
        this.dialogRef = null;
        this.toastrApiService.show('success', 'Device has been revoked successfully!');
        this.loadPasskeys();
        return;
      }
      this.dialog.closeAll();
      this.dialogRef = null;
      this.isRevoking = false;
      this.deletingId = null;
      this.selectedDevice = null;
      this.toastrApiService.show('error', error?.error?.message || 'Unable to revoke device.');
    }).finally(() => {
      this.isRevoking = false;
      this.deletingId = null;
      this.selectedDevice = null;
    });
  }
}
